import React, { useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { Table } from "../../../components/table";
import SLLogo from "../../../icons/srilanka.svg";
import QatarLogo from "../../../icons/qutar.svg";
import IndiaLogo from "../../../icons/india.svg";
import UsaLogo from "../../../icons/usa.svg";
import HongKongLogo from "../../../icons/hongkong.svg";
import NasdaqLogo from "../../../icons/nasdaqs.svg";
import SaudiLogo from "../../../icons/saudi.svg";
import DubaiLogo from "../../../icons/dubai.svg";
import AbudhabiLogo from "../../../icons/abudb.svg";
import KuwaitLogo from "../../../icons/kuwait.svg";
import { StarIcon } from "../../../components//StarIcon";

const exchangeDetails: any = {
  "Colombo Stock Exchange": { logo: SLLogo, shortCode: "CSE" },
  "New York Stock Exchange": { logo: UsaLogo, shortCode: "NYSE" },
  "HongKong Stock Exchange": { logo: HongKongLogo, shortCode: "HKEX" },
  "Qatar Stock Exchange": { logo: QatarLogo, shortCode: "QSE" },
  "Bombay Stock Exchange": { logo: IndiaLogo, shortCode: "BSE" },
  "National Association of Securities Dealers Automated Quotations": {
    logo: NasdaqLogo,
    shortCode: "NASDAQ",
  },
  "Saudi Stock Exchange": { logo: SaudiLogo, shortCode: "SSE" },
  "Dubai Financial Market": { logo: DubaiLogo, shortCode: "DFM" },
  "Abu Dhabi Securities Exchange": { logo: AbudhabiLogo, shortCode: "ADSX" },
  "Boursa Kuwait": { logo: KuwaitLogo, shortCode: "BK" },
  "Indian Stock Exchange": { logo: IndiaLogo, shortCode: "ISE" },
};

type DataTableType = {
  data: any[];
  loading: boolean;
  onStarClick?: (stock: any, event: React.MouseEvent) => void;
  starLoading?: boolean;
};

export const DataTable = (props: DataTableType) => {
  const [tableHeight, setTableHeight] = useState("calc(100vh - 400px)");
  const { data, loading, onStarClick, starLoading = false } = props;

  console.log("DataTableType", props.data);

  function formatNumber(value: any) {
    if (value === null || value === undefined || isNaN(value)) {
      return "-";
    }

    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function formatAmounts(value: any) {
    if (value === null || value === undefined || isNaN(value)) {
      return "-";
    }

    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  const addSorting = (column: any) => ({
    ...column,
    sorter: (a: any, b: any) => {
      let valueA = a[column.dataIndex];
      let valueB = b[column.dataIndex];

      if (valueA === "-") valueA = 0.0;
      if (valueB === "-") valueB = 0.0;

      // Handle null/undefined values
      if (valueA === null || valueA === undefined) return -1;
      if (valueB === null || valueB === undefined) return 1;

      // Convert to numbers and compare
      return Number(valueA) - Number(valueB);
    },
  });

  const columns: any = [
    {
      key: "star",
      title: "",
      dataIndex: "star",
      width: 50,
      fixed: window.innerWidth >= 50 ? "left" : undefined,
      render: (_: any, record: any) => (
        <StarIcon
          filled={true}
          className={`cursor-pointer transition-colors duration-200 ${
            starLoading
              ? "text-gray-300 cursor-not-allowed"
              : "text-yellow-500 hover:text-yellow-600"
          }`}
          onClick={
            starLoading || !onStarClick
              ? undefined
              : (event) => onStarClick(record, event)
          }
        />
      ),
    },
    {
      key: "ticker_symbol",
      title: "Ticker",
      dataIndex: "ticker_symbol",
      width: 100,
      fixed: window.innerWidth >= 50 ? "left" : undefined,
    },
    {
      key: "company_name",
      title: "Company",
      dataIndex: "company_name",
      width: 200,
    },
    {
      key: "market_name",
      title: "Exchange",
      dataIndex: "market_name",
      width: 150,
      render: (exchange: any) => (
        <div className="flex items-center gap-2">
          <img
            src={exchangeDetails[exchange]?.logo || QatarLogo}
            alt={exchange}
            className="w-4 h-4"
          />
          <span className="text-nowrap">
            {exchangeDetails[exchange]?.shortCode}
          </span>
        </div>
      ),
    },
    {
      key: "sector_name",
      title: "Sector",
      dataIndex: "sector_name",
      width: 150,
    },
    addSorting({
      key: "traded_price",
      title: "Last Traded Price",
      dataIndex: "traded_price",
      width: 150,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatNumber(value)}
          </h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "open_price",
      title: "Open Price",
      dataIndex: "open_price",
      width: 150,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatNumber(value)}
          </h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "pe_ratio",
      title: "P/E Ratio",
      dataIndex: "pe_ratio",
      width: 100,
      align: "right",
      render: (value: any) => (
        <span>
          {typeof value === "number"
            ? value >= 0
              ? value.toFixed(2)
              : "NaN"
            : "0.00"}
        </span>
      ),
    }),
    addSorting({
      key: "pricesToBook",
      title: "Price to Book",
      dataIndex: "price_to_book",
      width: 120,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">{formatNumber(value)}</h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "market_cap",
      title: "Market Capitalization",
      dataIndex: "market_cap",
      width: 150,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatAmounts(value)}
          </h1>
        ) : (
          "-"
        ),
    }),
  ];

  return (
    <div className="border rounded-lg shadow-sm vi-wl-card">
      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={props?.data ? props.data : []}
          pagination={{
            total: props?.data?.length,
            position: ["bottomCenter"],
            showSizeChanger: true,
            pageSizeOptions: [50, 100, 200],
            defaultPageSize: 100,
            prevIcon: (
              <div className="flex items-center gap-x-2">
                <FaArrowLeft className="text-[#667085]" />
                <h3 className="text-[#667085]">Previous</h3>
              </div>
            ),
            nextIcon: (
              <div className="flex items-center gap-x-2">
                <h3 className="text-[#667085]">Next</h3>
                <FaArrowRight className="text-[#667085]" />
              </div>
            ),
          }}
          scroll={{ x: "max-content", y: tableHeight }}
          rowKey="srNo"
          className="w-full"
          loading={props.loading}
        />
      </div>
    </div>
  );
};
